<script setup lang="ts">
import { ref, onMounted, onUnmounted } from "vue";
import WrButton from "~/components/global/WrButton.vue";
import WrVaahAgentsOrbitRings from "./WrVaahAgentsOrbitRings.vue";

export interface VaahPill {
  label: string;
  x?: string;
  y?: string;
  w?: string;
}

interface Props {
  heading?: string;
  ctaText?: string;
  ctaLink?: string;
  centerImage?: string;
  pills?: VaahPill[];
}

withDefaults(defineProps<Props>(), {
  heading: "Why Choose WebReinvent?",
  ctaText: "Get Started Instantly",
  ctaLink: "",
  centerImage: "/images/wri-genic.webp",
  pills: () => [],
});

const emit = defineEmits<{
  (e: "cta-clicked"): void;
}>();

const sectionRef = ref<HTMLElement | null>(null);

const logoTranslateY = ref(0);

let lastScrollY = 0;

const handleScroll = () => {
  if (!sectionRef.value) return;

  const rect = sectionRef.value.getBoundingClientRect();
  const currentScrollY = window.scrollY;

  if (rect.top < window.innerHeight && rect.bottom > 0) {
    if (currentScrollY > lastScrollY) {
      logoTranslateY.value = 24;
    } else if (currentScrollY < lastScrollY) {
      logoTranslateY.value = 0;
    }
  }

  lastScrollY = currentScrollY;
};

onMounted(() => {
  lastScrollY = window.scrollY;
  window.addEventListener("scroll", handleScroll, { passive: true });
});

onUnmounted(() => {
  window.removeEventListener("scroll", handleScroll);
});
</script>

<template>
  <section ref="sectionRef" class="vaah-why-choose relative flex flex-col items-center justify-center max-sm:gap-3">
    
    <!-- Desktop/Tablet Ring Stage -->
    <div class="pointer-events-none relative my-auto hidden aspect-square w-[min(86vw,900px)] select-none md:block">
      
      <!-- Concentric Rings & Arc Shimmers -->
      <div class="vaah-why-rings absolute inset-0" aria-hidden="true">
        <WrVaahAgentsOrbitRings />
      </div>

      <!-- Stage Upper Heading + Center Logo -->
      <div>
        <h2 class="font-avant-demi absolute left-1/2 top-[38%] z-30 max-w-[12rem] -translate-x-1/2 -translate-y-1/2 bg-gradient-to-r from-[#0e7490] to-[#7c3aed] bg-clip-text text-center text-xl font-semibold leading-tight text-transparent dark:from-[#12DBFF] dark:to-[#895AF5] lg:max-w-[14rem] lg:text-3xl">
          {{ heading }}
        </h2>

        <!-- Animated Logo: Smoothly moves down on scroll down and up on scroll up -->
        <NuxtImg
          :src="centerImage"
          alt="WebReinvent"
          width="630"
          height="467"
          loading="eager"
          class="absolute left-1/2 top-1/2 z-10 h-auto w-[120px] transition-transform duration-500 ease-out will-change-transform lg:w-[144px]"
          :style="{
            transform: `translate(-50%, calc(-50% + ${logoTranslateY}px))`
          }"
        />
      </div>

      <!-- Glassmorphism Panel -->
      <div class="vaah-why-panel pointer-events-auto absolute left-1/2 top-[54.5%] z-20 hidden h-[36%] w-[90%] -translate-x-1/2 flex-col items-center overflow-hidden px-[40px] pb-0 pt-[46px] md:flex">
        <span class="vaah-why-panel-top-glow" aria-hidden="true"></span>
        <p class="vaah-why-panel-subtitle font-avant max-w-[30rem] text-center text-[1.0625rem] leading-[1.6]">
          Run <strong>autonomous AI workflows</strong> directly on your codebase with live updates and total control.
        </p>
        <div class="mt-7 flex justify-center md:mt-12">
          <WrButton :link="ctaLink || undefined" append-class="px-7 py-2.5 text-base" @click="emit('cta-clicked')">
            {{ ctaText }}
          </WrButton>
        </div>
      </div>

      <!-- Orbiting Feature Pills (Untouched original logic) -->
      <div class="absolute inset-0 z-40 hidden md:block">
        <span
          v-for="pill in pills"
          :key="pill.label"
          class="vaah-why-pill font-avant absolute flex -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-[10px] px-3 py-2 text-center text-[0.75rem] font-medium tracking-[0.02em] lg:text-[0.8rem]"
          :style="{ left: pill.x, top: pill.y, '--pill-w': pill.w }"
        >
          {{ pill.label }}
        </span>
      </div>
    </div>

    <!-- Mobile Layout -->
    <div class="relative z-10 mx-auto mb-3 px-4 pb-3 text-center md:hidden">
      <h2 class="font-avant-demi bg-gradient-to-r from-[#0e7490] to-[#7c3aed] bg-clip-text text-3xl font-semibold leading-tight text-transparent dark:from-[#12DBFF] dark:to-[#895AF5]">
        {{ heading }}
      </h2>
      <div>
        <p class="font-avant text-base leading-relaxed text-[#5A5478]/85 dark:text-white/85">
          Run <strong>autonomous AI workflows</strong> directly on your codebase with live updates and total control.
        </p>
        <div class="mt-5 flex justify-center">
          <WrButton :link="ctaLink || undefined" append-class="px-7 py-2.5 text-base" @click="emit('cta-clicked')">
            {{ ctaText }}
          </WrButton>
        </div>
      </div>
    </div>

    <!-- Mobile Ring & Static Pill Grid -->
    <div class="relative mx-auto my-8 w-full max-w-md px-4 md:hidden">
      <div
        class="vaah-why-mobile-orbit pointer-events-none absolute left-1/2 top-1/2 aspect-square w-[min(96vw,380px)] -translate-x-1/2 -translate-y-1/2"
        aria-hidden="true"
      >
        <WrVaahAgentsOrbitRings />
      </div>

      <div class="relative flex flex-wrap justify-center gap-3">
        <span
          v-for="pill in pills"
          :key="'mobile-' + pill.label"
          class="vaah-why-pill font-avant flex min-w-0 items-center justify-center rounded-[10px] px-3.5 py-[0.55rem] text-center text-[0.75rem] font-medium tracking-[0.02em]"
        >
          {{ pill.label }}
        </span>
      </div>
    </div>
  </section>
</template>

<style scoped>
/* Ring Mask Filters */
.vaah-why-rings {
  -webkit-mask-image: linear-gradient(180deg, #000 35%, rgba(0, 0, 0, 0.35) 52%, transparent 70%);
  mask-image: linear-gradient(180deg, #000 35%, rgba(0, 0, 0, 0.35) 52%, transparent 70%);
}

.vaah-why-mobile-orbit {
  -webkit-mask-image: linear-gradient(180deg, #000 45%, rgba(0, 0, 0, 0.45) 65%, transparent 88%);
  mask-image: linear-gradient(180deg, #000 45%, rgba(0, 0, 0, 0.45) 65%, transparent 88%);
}

/* Glassmorphism Panel */
.vaah-why-panel::after {
  content: "";
  position: absolute;
  inset: 0;
  z-index: -1;
  backdrop-filter: blur(24px) saturate(1.4);
  -webkit-backdrop-filter: blur(24px) saturate(1.4);
  -webkit-mask-image: radial-gradient(ellipse 65% 85% at 50% 32%, #000 45%, transparent 80%);
  mask-image: radial-gradient(ellipse 65% 85% at 50% 32%, #000 45%, transparent 80%);
  pointer-events: none;
}

.vaah-why-panel::before {
  content: "";
  position: absolute;
  inset: 0;
  z-index: -1;
  background-image:
    radial-gradient(rgba(189, 197, 255, 0.12) 1px, rgba(189, 197, 255, 0.04) 2.5px, transparent 4px),
    linear-gradient(180deg, rgba(13, 20, 30, 0.3), rgba(0, 0, 0, 0.45));
  background-size: 11px 11px, 100% 100%;
  filter: blur(2px);
  -webkit-mask-image: radial-gradient(ellipse 65% 85% at 50% 32%, #000 45%, transparent 80%);
  mask-image: radial-gradient(ellipse 65% 85% at 50% 32%, #000 45%, transparent 80%);
  pointer-events: none;
}

.vaah-why-panel-top-glow {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 2px;
  background: linear-gradient(90deg, transparent, #bdc5ff 50%, transparent);
  filter: drop-shadow(0 0 6px rgba(189, 197, 255, 0.9)) drop-shadow(0 0 16px rgba(189, 197, 255, 0.5));
  pointer-events: none;
  transform-origin: center;
  animation: vaah-why-top-glow 4.5s ease-in-out infinite;
}

.vaah-why-panel-top-glow::after {
  content: "";
  position: absolute;
  top: 2px;
  left: 0;
  width: 100%;
  height: 90px;
  background: radial-gradient(ellipse 75% 100% at 50% 0%, rgba(189, 197, 255, 0.2), transparent 70%);
}

@keyframes vaah-why-top-glow {
  0% { transform: scaleX(0); opacity: 0; }
  15% { opacity: 1; }
  60% { transform: scaleX(1); opacity: 0.9; }
  100% { transform: scaleX(1); opacity: 0; }
}

.vaah-why-panel-subtitle { color: rgba(255, 255, 255, 0.85); }
.vaah-why-panel-subtitle strong { font-weight: 700; color: #fff; }

/* Pill Component Styling */
.vaah-why-pill {
  border: 1px solid #1c2b48;
  width: var(--pill-w, auto);
  box-shadow: inset 0 1px 18.9px #0e1a36;
  background-color: #000a1f;
  background-image:
    linear-gradient(94.65deg, #bdc5ff 3.76%, #3f4783 140.86%),
    linear-gradient(#000a1f, #000a1f);
  -webkit-background-clip: text, padding-box;
  background-clip: text, padding-box;
  color: transparent;
}

@media (max-width: 767px) {
  .vaah-why-pill {
    flex: 0 0 calc((100% - 1.5rem) / 3);
    min-width: 0;
  }
}

/* Light Theme Overrides */
.my-app-light .vaah-why-panel::before {
  background-image:
    radial-gradient(rgba(109, 90, 220, 0.18) 1px, rgba(109, 90, 220, 0.06) 2.5px, transparent 4px),
    linear-gradient(180deg, rgba(255, 255, 255, 0.3), rgba(243, 240, 255, 0.5));
}
.my-app-light .vaah-why-panel-subtitle { color: rgba(30, 27, 75, 0.85); }
.my-app-light .vaah-why-panel-subtitle strong { color: #4c1d95; }
.my-app-light .vaah-why-pill {
  border-color: rgba(124, 108, 246, 0.35);
  box-shadow: inset 0 1px 18.9px rgba(93, 70, 190, 0.15);
  background-color: #ffffff;
  background-image:
    linear-gradient(94.65deg, #501d71 3.76%, #4753dc 140.86%),
    linear-gradient(#ffffff, #ffffff);
}
.my-app-light .vaah-why-panel-top-glow {
  background: linear-gradient(90deg, transparent, #8b7bff 50%, transparent);
  filter: drop-shadow(0 0 6px rgba(139, 123, 255, 0.9)) drop-shadow(0 0 16px rgba(139, 123, 255, 0.5));
}
.my-app-light .vaah-why-panel-top-glow::after {
  background: radial-gradient(ellipse 75% 100% at 50% 0%, rgba(139, 123, 255, 0.25), transparent 70%);
}

@media (prefers-reduced-motion: reduce) {
  .vaah-why-panel-top-glow {
    animation: none;
  }
}
</style>